// Role-aware menu (spec §2, constitution §6.3). Visibility comes from the permissions the API sent
// in /me, which the API derives from ROLE_PERMISSIONS in packages/shared. The menu only hides
// what a role cannot use; the API still refuses every call the role is not allowed to make.
import { hasPermission, isLeadsRole, type Permission } from '@cis/shared';
import type { Me } from '@cis/api-client';

export type SectionPath =
  | '/'
  | '/start'
  | '/tasks'
  | '/verification'
  | '/evidence'
  | '/checklists'
  | '/gantt'
  | '/people'
  | '/leads'
  | '/files'
  | '/exports'
  | '/search'
  | '/admin';

export interface NavItem {
  readonly to: SectionPath;
  readonly label: string;
  readonly permission: Permission;
  /** A second permission that also opens the section (either one is enough). */
  readonly orPermission?: Permission;
  readonly group: 'main' | 'leads' | 'admin';
  /** Leads-only data (constitution §6.4): also requires a Leads role, not just the permission. */
  readonly leadsOnly?: true;
}

// Order is menu order. Search is offered with task.read: attesters see only Start here and their
// checklists (spec §2, P9), even though the search endpoint itself is open to every role.
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Home', permission: 'task.read', group: 'main' },
  { to: '/start', label: 'Start here', permission: 'start.read', group: 'main' },
  { to: '/tasks', label: 'Tasks', permission: 'task.read', group: 'main' },
  { to: '/verification', label: 'Verification', permission: 'task.verify', group: 'main' },
  { to: '/evidence', label: 'Evidence', permission: 'evidence.read', group: 'main' },
  { to: '/checklists', label: 'Checklists', permission: 'checklist.read', group: 'main' },
  { to: '/gantt', label: 'Gantt', permission: 'task.read', group: 'main' },
  { to: '/people', label: 'People', permission: 'people.read', group: 'main' },
  { to: '/files', label: 'Guides and files', permission: 'content.read', group: 'main' },
  // Each role downloads its own edition (spec J8): the team edition or the full one.
  {
    to: '/exports',
    label: 'Exports',
    permission: 'export.team',
    orPermission: 'export.full',
    group: 'main',
  },
  { to: '/search', label: 'Search', permission: 'task.read', group: 'main' },
  { to: '/leads', label: 'Leads', permission: 'leads.read', group: 'leads', leadsOnly: true },
  { to: '/admin', label: 'Admin', permission: 'user.manage', group: 'admin' },
];

/** Where a person with no access to any section is sent. */
export const NO_ACCESS_PATH = '/no-access';

/**
 * True only when /me lists the permission and ROLE_PERMISSIONS gives it to the role: a /me that
 * grants a role more than the shared matrix (bug, stale build, tampered response) fails closed.
 */
export function can(me: Me, permission: Permission): boolean {
  return me.permissions.includes(permission) && hasPermission(me.user.role, permission);
}

export function canSee(me: Me, item: NavItem): boolean {
  if (item.leadsOnly === true && !isLeadsRole(me.user.role)) return false;
  if (item.orPermission !== undefined && can(me, item.orPermission)) return true;
  return can(me, item.permission);
}

export function visibleNav(me: Me): NavItem[] {
  return NAV_ITEMS.filter((item) => canSee(me, item));
}

export function navItemFor(path: SectionPath): NavItem {
  const item = NAV_ITEMS.find((i) => i.to === path);
  if (item === undefined) throw new Error(`No menu entry for ${path}`);
  return item;
}

/** The first section this person may open: Home for the team, Start here for attesters. */
export function landingPath(me: Me): SectionPath | typeof NO_ACCESS_PATH {
  return visibleNav(me)[0]?.to ?? NO_ACCESS_PATH;
}
